import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader, CTASection } from "@/components/Sections";
import { Reveal } from "@/components/Reveal";
import { PARTS } from "@/lib/site";

export default function Parts() {
  useSEO({
    title: "Mining Spare & Wear Parts | BlackSilex",
    description: "Mining equipment spare parts and wear parts — ground engaging tools, screen media, conveyor components, remanufacturing and consumables, original and aftermarket.",
    path: "/parts",
  });

  return (
    <div data-testid="parts-page">
      <PageHeader
        crumbs={[{ label: "Home", to: "/" }, { label: "Parts" }]}
        eyebrow="What We Supply"
        titleLines={["Spare & wear", "parts."]}
        intro="Mining equipment spare parts and wear parts selected around the components that wear fastest — available as original or aftermarket."
      />

      <section className="max-w-[1600px] mx-auto px-6 lg:px-10 py-16 lg:py-24">
        <div className="grid md:grid-cols-2 border-t border-l border-offwhite/10">
          {PARTS.map((p) => (
            <Reveal key={p.id} delay={0.05}>
              <div id={p.id} className="group relative bg-coal scroll-mt-28 h-full p-8 lg:p-12 border-r border-b border-offwhite/10 hover:bg-nearblack transition-colors" data-testid={`part-card-${p.num}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono-tech text-xs text-strike tracking-[0.2em]">{p.num}</span>
                  <span className="font-mono-tech text-[0.65rem] text-mutedgray tracking-[0.2em] uppercase">{p.meta}</span>
                </div>
                <h2 className="font-display text-2xl lg:text-3xl font-bold uppercase tracking-tight text-offwhite mt-8">{p.title}</h2>
                <ul className="mt-6 flex flex-wrap gap-x-6 gap-y-2">
                  {p.items.map((it) => (
                    <li key={it} className="font-mono-tech text-xs uppercase tracking-[0.12em] text-mutedgray flex items-center gap-2">
                      <span className="w-1 h-1 bg-strike inline-block" /> {it}
                    </li>
                  ))}
                </ul>
                <Link
                  to={`/request-part?category=${encodeURIComponent(p.title)}`}
                  className="mt-10 inline-flex items-center gap-3 font-mono-tech text-xs uppercase tracking-[0.2em] text-offwhite group/link"
                  data-testid={`request-part-${p.num}`}
                >
                  <span className="link-underline">Request Part</span>
                  <ArrowUpRight className="w-4 h-4 text-strike group-hover/link:translate-x-1 group-hover/link:-translate-y-1 transition-transform" />
                </Link>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <CTASection title="TELL US WHAT YOU NEED." copy="Send the part number, brand and machine details — we respond with origin, lead time and pricing." />
    </div>
  );
}
